import { Dispatch, SetStateAction, useEffect, useState } from "react";
import Modal from "../Shared/Modal";
import ConfirmedImg from "../../assets/ConfirmedImg.svg";
import Button from "../Shared/Button";
import { Dates } from "../../api/Consultation/GetAllConsultants";
import { Plus } from "lucide-react";
import Input from "../Shared/Input";
import TextArea from "../Shared/TextArea";
import * as Yup from "yup";
import { useFormik } from "formik";
import { ButtonTypes } from "../../utils/consts";
import { BookConsultation } from "../../api/Consultation/ConsultationBooking";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { queryClient } from "../../main";
const validationSchema = Yup.object({
    name: Yup.string().required("Full name is required"),
    description: Yup.string().required("Consultation brief is required"),
    email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
    phone: Yup.string()
        .required("Mobile number is required")
        .max(15, "Phone number must not exceed 15 digits"),
});

type ModalProps = {
    isOpen: boolean;
    closeModal: Dispatch<SetStateAction<boolean>>;
    consultant?: {
        id: string;
        name: string;
        title: string;
        consultantDates: Dates[];
    } | null ;
};

const ScheduleModal = ({ isOpen, closeModal, consultant }: ModalProps) => {
    const [step, setStep] = useState(1); // Track the current step
    const [selectedDate, setSelectedDate] = useState<string | null>(null); // Track selected date
    const handleNextStep = () => {
        if (step < 3 && selectedDate) {
            setStep(step + 1);
        }
    };
    const consultantId = consultant?.id || ""
    useEffect(() => {
        if (!isOpen) {
            setStep(1); // Reset to step 1
            setSelectedDate(null); // Reset the selected date
        }
    }, [isOpen]);

    useEffect(() => {
        // If the selected date is available, update Formik's initial values dynamically
        if (selectedDate) {
            formik.setFieldValue("consultantDateId", selectedDate); // Dynamically set the selected date value in Formik
        }
        if (consultantId) {
            formik.setFieldValue("consultantId", consultantId); // Dynamically set the selected date value in Formik
        }

    }, [selectedDate , consultantId]);

    const { mutate, error } = useMutation({
        mutationFn: BookConsultation,
        onSuccess: () => {
            handleNextStep()
            queryClient.invalidateQueries({ queryKey: ["consultants"] });
        },
        onError: () => {
            formik.setSubmitting(false);
        },
    });

    const handleSelectDate = (date: string) => {
        setSelectedDate(date); // Update the selected date when a user selects a date
    };

    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            description: "",
            phone: "",
            consultantDateId: selectedDate || "",
            consultantId: consultantId,
        },
        validationSchema,
        onSubmit: (values) => {
            mutate(values);
        },
    });

    return (
        <Modal className={"!max-w-[671px] min-h-[750px]"} isOpen={isOpen} closeModal={closeModal}>
            <div className="flex flex-col items-center h-full ju px-7 py-12 md:py-16">
                <div className="max-w-lg w-full text-center h-full flex flex-col justify-between">
                    {step === 1 && (
                        <>
                            <div className="border border-[#E4E4E4] drop-shadow-sm rounded-lg pt-4 pb-5 px-3 mb-8">
                                <div className="flex justify-between mb-6 items-center">
                                    <h1 className="font-medium">Select time</h1>
                                    <Plus size={16} className="text-[#3D50F8]" />
                                </div>
                                <div className="grid grid-cols-3 gap-x-2 gap-y-2.5">
                                    {consultant?.consultantDates?.map((date) => (
                                        <button
                                            disabled={!date.isAvailable}
                                            key={date.id}
                                            onClick={() => handleSelectDate(date.id as string)}
                                            className={`${date.isAvailable ? '' : '!bg-[#808080]'}  ${selectedDate === date.id
                                                ? "bg-secondary text-white" // Selected date style
                                                : "bg-[#5272E9] text-white"
                                                } rounded-md font-semibold py-2 transition-all duration-300`}
                                        >
                                            <p>{date.day}</p>
                                            <span className="text-sm">{date.time}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <Button disabled={!selectedDate}
                                onClick={handleNextStep} variant="primary" className={` mt-auto !h-[42px] !text-base w-full ${selectedDate ? '!bg-[#3D50F8] hover:!bg-[#1a2159]' : '!bg-[#3D50F8]/70'}`}>
                                Schedule a Session
                            </Button>
                        </>
                    )}

                    {step === 2 && (
                        <form className="h-full flex flex-col justify-between" onSubmit={formik.handleSubmit}>
                            <div className="text-start">
                                <p className="text-[#808080] tracking-[-0.02em] mb-8 md:text-xl">
                                    Please confirm your details before proceeding to finalize the session.
                                </p>
                                <div className="space-y-5">
                                    <Input value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.name && formik.errors.name
                                                ? formik.errors.name
                                                : ""
                                        }
                                        name="name" title="Full name" inputStyles="!h-12 !border-[#D0D5DD] hover:!border-secondary focus:!border-secondary !bg-transparent" />
                                    <Input value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.email && formik.errors.email
                                                ? formik.errors.email
                                                : ""
                                        }
                                        name="email" title="Email" inputStyles="!h-12 !border-[#D0D5DD] hover:!border-secondary focus:!border-secondary !bg-transparent" />
                                    <Input value={formik.values.phone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.phone && formik.errors.phone
                                                ? formik.errors.phone
                                                : ""
                                        }
                                        name="phone" title="Phone Number" inputStyles="!h-12 !border-[#D0D5DD] hover:!border-secondary focus:!border-secondary !bg-transparent" />
                                    <TextArea value={formik.values.description}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.description && formik.errors.description
                                                ? formik.errors.description
                                                : ""
                                        }
                                        name="description" placeholder="" title="Consultation Brief" inputStyles="!h-28 !border-[#D0D5DD] hover:!border-secondary focus:!border-secondary !bg-transparent" />

                                </div>
                            </div>
                            {error instanceof AxiosError && (
                                <span className="text-red-500 font-bold text-lg mt-4">
                                    {typeof error?.response?.data?.message === "string"
                                        ? error?.response?.data?.error
                                        : "An error occurred"}
                                </span>
                            )}

                            <Button disabled={formik.isSubmitting || !formik.isValid} type={ButtonTypes.SUBMIT} variant="primary" className="!bg-[#3D50F8] mt-10 hover:!bg-[#1a2159] !h-[42px] !text-base w-full">
                            Confirm Details
                            </Button>
                        </form>
                    )}

                    {step === 3 && (
                        <>
                            <h1 className="text-3xl font-semibold mb-2 tracking-[-0.02em] sm:text-4xl">
                                Session Confirmed
                            </h1>
                            <p className="text-[#808080] tracking-[-0.02em] mb-12 md:text-xl">
                                Your subscription has been activated. You can now access all the features of our course.
                            </p>
                            <img src={ConfirmedImg} className="mx-auto mb-24" alt="Confirmed" />
                            <Button onClick={() => closeModal(false)} variant="primary" className="!bg-[#3D50F8] hover:!bg-[#1a2159] !h-[42px] !text-base w-full">
                                Done
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default ScheduleModal;
